/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  Condition,
  Condition$inboundSchema,
  Condition$Outbound,
  Condition$outboundSchema,
} from "./condition.js";
import {
  ConditionGroup,
  ConditionGroup$inboundSchema,
  ConditionGroup$Outbound,
  ConditionGroup$outboundSchema,
} from "./conditiongroup.js";
import {
  NestedConditionExpression,
  NestedConditionExpression$inboundSchema,
  NestedConditionExpression$Outbound,
  NestedConditionExpression$outboundSchema,
} from "./nestedconditionexpression.js";

/**
 * Defines a condition expression. Can be a single condition, a group of conditions, or nested groups. Exactly one of 'condition', 'condition_group', or 'nested_expression' must be specified.
 */
export type ConditionExpression = {
  /**
   * Defines a single condition that compares a source value against a target value.
   */
  condition?: Condition | undefined;
  /**
   * Defines a group of conditions combined with a logical operator (AND/OR).
   */
  conditionGroup?: ConditionGroup | undefined;
  /**
   * Optional name for the condition, useful for debugging and documentation.
   */
  name?: string | undefined;
  /**
   * Allows for nested condition logic like (A AND B) OR (C AND D).
   */
  nestedExpression?: NestedConditionExpression | undefined;
};

/** @internal */
export const ConditionExpression$inboundSchema: z.ZodType<
  ConditionExpression,
  z.ZodTypeDef,
  unknown
> = z.object({
  condition: types.optional(Condition$inboundSchema),
  condition_group: types.optional(ConditionGroup$inboundSchema),
  name: types.optional(types.string()),
  nested_expression: types.optional(NestedConditionExpression$inboundSchema),
}).transform((v) => {
  return remap$(v, {
    "condition_group": "conditionGroup",
    "nested_expression": "nestedExpression",
  });
});
/** @internal */
export type ConditionExpression$Outbound = {
  condition?: Condition$Outbound | undefined;
  condition_group?: ConditionGroup$Outbound | undefined;
  name?: string | undefined;
  nested_expression?: NestedConditionExpression$Outbound | undefined;
};

/** @internal */
export const ConditionExpression$outboundSchema: z.ZodType<
  ConditionExpression$Outbound,
  z.ZodTypeDef,
  ConditionExpression
> = z.object({
  condition: Condition$outboundSchema.optional(),
  conditionGroup: ConditionGroup$outboundSchema.optional(),
  name: z.string().optional(),
  nestedExpression: NestedConditionExpression$outboundSchema.optional(),
}).transform((v) => {
  return remap$(v, {
    conditionGroup: "condition_group",
    nestedExpression: "nested_expression",
  });
});

export function conditionExpressionToJSON(
  conditionExpression: ConditionExpression,
): string {
  return JSON.stringify(
    ConditionExpression$outboundSchema.parse(conditionExpression),
  );
}
export function conditionExpressionFromJSON(
  jsonString: string,
): SafeParseResult<ConditionExpression, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ConditionExpression$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ConditionExpression' from JSON`,
  );
}
